import React, { useState, useEffect } from 'react';
import { useCasesApi } from '../hooks/useCasesApi';
import { Case } from '../hooks/useCasesApi';


// Helper function to split full name into first and last name
const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  if (!fullName || fullName.trim() === '') {
    return { firstName: '', lastName: '' };
  }
  
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }
  
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
};

// Helper function to combine first and last name
const combineNames = (firstName: string, lastName: string): string => {
  const first = firstName || '';
  const last = lastName || '';
  
  if (!first && !last) return '';
  if (!last) return first;
  if (!first) return last;
  return `${first} ${last}`;
};


interface EditCaseProps {
  caseData: Case;
  onClose: () => void;
  onUpdate: () => void;
}

interface EditCaseFormData {
  clientName: string; // Keep for backward compatibility
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  caseTitle: string;
  caseDescription: string;
  country: string;
  caseType: string;
  priority: string;
}

export default function EditCase({ caseData, onClose, onUpdate }: EditCaseProps) {
  const { updateCase, forceRefresh } = useCasesApi();
  
  const [formData, setFormData] = useState<EditCaseFormData>(() => {
    const { firstName, lastName } = splitFullName(caseData.clientName);
    return {
      clientName: caseData.clientName, // Keep for backward compatibility
      clientFirstName: firstName,
      clientLastName: lastName,
      clientEmail: caseData.clientEmail,
      caseTitle: caseData.title || '',
      caseDescription: caseData.description || '',
      country: caseData.country || 'USA',
      caseType: caseData.caseType,
      priority: caseData.priority
    };
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Case types by country
  const caseTypesByCountry = {
    USA: [
      'Family Visa (I-130)',
      'Work Visa (H-1B)',
      'Student Visa (F-1)',
      'Business Visa (E-2)',
      'Green Card (I-485)',
      'Citizenship (N-400)',
      'Asylum (I-589)',
      'Other (USA)'
    ],
    Canada: [
      'Study Permit',
      'Work Permit',
      'Visitor Visa',
      'Business Immigration',
      'Citizenship',
      'Refugee Claim',
      'Other (Canada)'
    ]
  };

  const handleInputChange = (field: keyof EditCaseFormData, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Reset case type when country changes
      if (field === 'country') {
        const availableCaseTypes = caseTypesByCountry[value as keyof typeof caseTypesByCountry];
        newData.caseType = availableCaseTypes[0];
      }
      
      return newData;
    });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.clientName.trim() || !formData.clientEmail.trim() || !formData.caseTitle.trim()) {
        throw new Error('Please fill in all required fields');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.clientEmail)) {
        throw new Error('Please enter a valid email address');
      }

      // Only send fields that have actually changed
      const updates: any = {};
      
      if (formData.clientName !== caseData.clientName) {
        updates.clientName = formData.clientName;
      }
      
      if (formData.clientEmail !== caseData.clientEmail) {
        updates.clientEmail = formData.clientEmail;
      }
      
      if (formData.caseTitle !== caseData.title) {
        updates.title = formData.caseTitle;
      }
      
      if (formData.caseDescription !== caseData.description) {
        updates.description = formData.caseDescription;
      }
      
      if (formData.country !== caseData.country) {
        updates.country = formData.country;
      }
      
      if (formData.priority && formData.priority !== caseData.priority) {
        updates.priority = formData.priority;
      }

      // Only update if there are actual changes
      if (Object.keys(updates).length === 0) {
        console.log('No changes detected, skipping update');
        onClose();
        return;
      }

      await updateCase(caseData.id, updates);

      // Force a refresh from the database
      await forceRefresh();
      
      // Call onUpdate to trigger parent component refresh
      onUpdate();
      
      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error in EditCase form:', err);
      const errorMessage = err instanceof Error ? err.message : 
        (typeof err === 'string' ? err : 'Failed to update case');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-case-overlay">
      <div className="edit-case-modal">
        <div className="modal-header">
          <h2>Edit Case</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-case-form" style={{ padding: '0.75rem 0.75rem 0.25rem 0.75rem', maxWidth: '95%' }}>
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-section" style={{ marginBottom: '0.75rem' }}>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Client Information</h3>
            
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="clientFirstName">First Name *</label>
              <input
                type="text"
                id="clientFirstName"
                value={formData.clientFirstName}
                onChange={(e) => handleInputChange('clientFirstName', e.target.value)}
                placeholder="Enter client's first name"
                required
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="clientLastName">Last Name</label>
              <input
                type="text"
                id="clientLastName"
                value={formData.clientLastName}
                onChange={(e) => handleInputChange('clientLastName', e.target.value)}
                placeholder="Enter client's last name (optional)"
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              />
              <small style={{ color: '#666', fontSize: '0.8rem' }}>
                Leave empty or enter "N/A" if no last name
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="clientEmail">Client Email *</label>
              <input
                type="email"
                id="clientEmail"
                value={formData.clientEmail}
                onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                placeholder="Enter client's email address"
                required
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div className="form-section" style={{ marginBottom: '0.75rem' }}>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>Case Details</h3>
            
            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="caseTitle">Case Title *</label>
              <input
                type="text"
                id="caseTitle"
                value={formData.caseTitle}
                onChange={(e) => handleInputChange('caseTitle', e.target.value)}
                placeholder="Enter a descriptive title for the case"
                required
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="country">Country *</label>
              <select
                id="country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                required
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              >
                <option value="USA">United States</option>
                <option value="Canada">Canada</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="caseType">Case Type</label>
              <select
                id="caseType"
                value={formData.caseType}
                disabled
                style={{ backgroundColor: '#f5f5f5', color: '#666', maxWidth: '100%', boxSizing: 'border-box' }}
              >
                {caseTypesByCountry[formData.country as keyof typeof caseTypesByCountry]?.map((caseType) => (
                  <option key={caseType} value={caseType}>
                    {caseType}
                  </option>
                ))}
              </select>
              <small style={{ color: '#666', fontStyle: 'italic' }}>
                Case type cannot be changed after creation.
              </small>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="priority">Priority <span style={{fontWeight:400}}>(Optional)</span></label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              >
                <option value="">Select priority (optional)</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: '0.5rem' }}>
              <label htmlFor="caseDescription">Case Description</label>
              <textarea
                id="caseDescription"
                value={formData.caseDescription}
                onChange={(e) => handleInputChange('caseDescription', e.target.value)}
                placeholder="Provide details about the case, requirements, and any special considerations"
                rows={3}
                style={{ maxWidth: '100%', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          <div className="form-actions" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', width: 'calc(100% - 1.5rem)', padding: '0 0.75rem 1rem 0.75rem', boxSizing: 'border-box' }}>
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={loading}
              style={{ minWidth: '90px', maxWidth: '120px', flex: '0 0 auto' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
              style={{ minWidth: '90px', maxWidth: '120px', flex: '0 0 auto' }}
            >
              {loading ? 'Updating Case...' : 'Update Case'}
            </button>
          </div>


        </form>
      </div>
    </div>
  );
} 